import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState, FunctionComponent } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useMultiElementIsFocused } from '../../hooks/useMultiElementIsFocused/useMultiElementIsFocused';
import { useRefWithCurrentValue } from '../../hooks/useRefWithCurrentValue';
import { InputLayout, InputLayoutProps } from '../../input/InputLayout/InputLayout';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { DateInput } from '../DateInput/DateInput';
import { TimeInput, TimeInputRef } from '../TimeInput/TimeInput';
import styles from './DateTimeInput.module.css';
export type DateTimeInputProps = PropsWithoutChildren<Omit<InputLayoutProps, 'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture' | 'multiline'>> & {
  value: Date | null;
  onInput: (value: Date | null) => void;
};
export const DateTimeInput: FunctionComponent<DateTimeInputProps> = React.memo(props => {
  const $ = _c(33);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let onInput;
  let remainingProps;
  let value;
  if ($[2] !== t0) {
    ({
      value,
      onInput,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = onInput;
    $[4] = remainingProps;
    $[5] = value;
  } else {
    onInput = $[3];
    remainingProps = $[4];
    value = $[5];
  }
  const timeInputRef = useRef(null);
  const [bufferedValue, setBufferedValue] = useState(value);
  const bufferedValueRef = useRefWithCurrentValue(bufferedValue);
  let t1;
  if ($[6] !== value) {
    t1 = () => {
      setBufferedValue(value);
    };
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== bufferedValueRef || $[9] !== onInput || $[10] !== value) {
    t2 = () => {
      if (bufferedValueRef.current?.getTime() !== value?.getTime()) {
        onInput(bufferedValueRef.current);
      }
    };
    $[8] = bufferedValueRef;
    $[9] = onInput;
    $[10] = value;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = {
      onFocus: t1,
      onBlur: t2
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const {
    isFocused,
    focusHandlerFactory,
    blurHandlerFactory
  } = useMultiElementIsFocused(t3);
  let t4;
  if ($[15] !== bufferedValueRef || $[16] !== isFocused || $[17] !== onInput) {
    t4 = newValue => {
      if (isFocused) {
        bufferedValueRef.current = newValue;
        setBufferedValue(newValue);
      } else {
        onInput(newValue);
      }
    };
    $[15] = bufferedValueRef;
    $[16] = isFocused;
    $[17] = onInput;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const acceptNewValue = useEventCallback(t4);
  const handleDateInput = acceptNewValue;
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      timeInputRef.current?.focus();
    };
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleDateInputFinished = useEventCallback(t5);
  let t6;
  if ($[20] !== acceptNewValue || $[21] !== bufferedValueRef) {
    t6 = (hours, minutes) => {
      assertNotNullOrUndefined(bufferedValueRef.current, "no value available");
      let newValue_0 = null;
      if (hours != null && minutes != null) {
        newValue_0 = new Date(bufferedValueRef.current);
        newValue_0.setHours(hours);
        newValue_0.setMinutes(minutes);
      }
      acceptNewValue(newValue_0);
    };
    $[20] = acceptNewValue;
    $[21] = bufferedValueRef;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const handleTimeInput = useEventCallback(t6);
  const displayedValue = isFocused ? bufferedValue : value;
  let t7;
  if ($[23] !== blurHandlerFactory || $[24] !== displayedValue || $[25] !== focusHandlerFactory || $[26] !== handleDateInput || $[27] !== handleDateInputFinished || $[28] !== handleTimeInput) {
    t7 = () => <div className={styles.inputsWrapper}><DateInput label="" value={displayedValue} onInput={handleDateInput} onFocus={focusHandlerFactory("DateInput")} onBlur={blurHandlerFactory("DateInput")} onInputFinished={handleDateInputFinished} data-testid="DateTimeInput.DateInput" /><TimeInput ref={timeInputRef} label="" hours={displayedValue?.getHours() ?? null} minutes={displayedValue?.getMinutes() ?? null} onInput={handleTimeInput} onFocus={focusHandlerFactory("TimeInput")} onBlur={blurHandlerFactory("TimeInput")} readOnly={!displayedValue} data-testid="DateTimeInput.TimeInput" /></div>;
    $[23] = blurHandlerFactory;
    $[24] = displayedValue;
    $[25] = focusHandlerFactory;
    $[26] = handleDateInput;
    $[27] = handleDateInputFinished;
    $[28] = handleTimeInput;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== remainingProps || $[31] !== t7) {
    t8 = <InputLayout {...remainingProps} multiline={true}>{t7}</InputLayout>;
    $[30] = remainingProps;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
});